import { displayValue, formDefinitionSchema, inputFields } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Code2, Copy, FileText, Inbox, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Input, Skeleton, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { FormItem, FormSubmissionItem, Paginated } from '@/lib/types';
import { cn, formatDateTime, formatRelative } from '@/lib/utils';

export const ACCESS_LABELS = { PUBLIC: 'Öffentlich', PORTAL: 'Kundenportal', INTERNAL: 'Intern' } as const;
export const KIND_LABELS = { REQUEST: 'Anfrage', CHECKLIST: 'Checkliste' } as const;
const SOURCE_LABELS = { PUBLIC: 'Website-Link', EMBED: 'Eingebettet', PORTAL: 'Kundenportal', INTERNAL: 'Intern' } as const;

export function useForms() {
  return useQuery({ queryKey: ['forms'], queryFn: () => api<FormItem[]>('/forms') });
}

export function publicFormUrl(slug: string, tenantSlug: string) {
  return `${window.location.origin}/f/${slug}?tenant=${encodeURIComponent(tenantSlug)}`;
}

export function EmbedDialog({ form, open, onOpenChange }: { form: FormItem; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { me } = useAuth();
  const url = publicFormUrl(form.slug, me?.tenant.slug ?? '');
  const frameId = `sp-form-${form.slug}`;
  const code = `<iframe id="${frameId}" src="${url}&embed=1" title="${form.name.replace(/"/g, '&quot;')}" style="width:100%;border:0;min-height:480px" loading="lazy"></iframe>
<script>
  window.addEventListener('message', function (e) {
    if (e.origin === '${window.location.origin}' && e.data && e.data.type === 'serviceportal:height') {
      document.getElementById('${frameId}').style.height = e.data.height + 'px';
    }
  });
</script>`;
  const copy = async (text: string, what: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} kopiert`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Formular teilen und einbetten</DialogTitle>
          <DialogDescription>Der Link öffnet das Formular im Design Ihres Unternehmens. Der Code bindet es auf einer beliebigen Website ein; die Höhe passt sich automatisch an.</DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          {!form.publishedVersion && <p className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">Das Formular ist noch nicht veröffentlicht.</p>}
          <Field label="Öffentlicher Link">
            <div className="flex gap-2">
              <Input readOnly value={url} className="font-mono text-xs" />
              <Button variant="outline" onClick={() => copy(url, 'Link')}>
                <Copy /> Kopieren
              </Button>
            </div>
          </Field>
          <Field label="Code zum Einbetten">
            <Textarea readOnly rows={9} value={code} className="font-mono text-xs" />
          </Field>
          <Button variant="outline" className="w-fit" onClick={() => copy(code, 'Code')}>
            <Code2 /> Code kopieren
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<string>('');
  const { data: templates } = useQuery({
    queryKey: ['forms', 'templates'],
    queryFn: () => api<{ key: string; name: string; description: string; kind: keyof typeof KIND_LABELS; access: keyof typeof ACCESS_LABELS }[]>('/forms/templates'),
  });
  const create = useMutation({
    mutationFn: () => api<FormItem>('/forms', { method: 'POST', body: { name, templateKey: template || undefined } }),
    onSuccess: (f) => navigate(`/forms/${f.id}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const options = [{ key: '', name: 'Leeres Formular', description: 'Mit einer leeren Seite beginnen.', kind: 'REQUEST' as const, access: 'PORTAL' as const }, ...(templates ?? [])];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Neues Formular</DialogTitle>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          <Field label="Name" htmlFor="nf-name">
            <Input id="nf-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="z. B. Serviceanfrage Website" />
          </Field>
          <div className="grid gap-2 sm:grid-cols-2">
            {options.map((t) => (
              <button
                key={t.key || 'blank'}
                type="button"
                onClick={() => {
                  setTemplate(t.key);
                  if (!name && t.key) setName(t.name);
                }}
                className={cn('rounded-lg border p-3 text-left transition-colors', template === t.key ? 'border-primary bg-primary/5' : 'hover:bg-muted/40')}
              >
                <span className="block text-sm font-medium">{t.name}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{t.description}</span>
                <span className="mt-2 flex gap-1">
                  <Badge variant="secondary">{KIND_LABELS[t.kind]}</Badge>
                  <Badge variant="outline">{ACCESS_LABELS[t.access]}</Badge>
                </span>
              </button>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={() => create.mutate()} disabled={name.trim().length < 2} loading={create.isPending}>
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FormsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [embed, setEmbed] = useState<FormItem | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const { data, isLoading } = useForms();
  const canManage = can('forms.manage');
  const onError = (e: unknown) => toast.error(errorMessage(e));
  const duplicate = useMutation({
    mutationFn: (f: FormItem) => api<FormItem>('/forms', { method: 'POST', body: { name: `${f.name} (Kopie)`, copyFromId: f.id } }),
    onSuccess: (f) => navigate(`/forms/${f.id}`),
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/forms/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }), onError });

  return (
    <>
      {confirmDialog}
      <PageHeader
        title="Formulare"
        description="Anfrageformulare für Website und Kundenportal sowie interne Checklisten"
        actions={
          canManage && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus /> Neues Formular
            </Button>
          )
        }
      />
      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Formular</TH>
              <TH className="hidden md:table-cell">Art</TH>
              <TH>Status</TH>
              <TH className="hidden text-right sm:table-cell">Einsendungen</TH>
              <TH className="w-12" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={5}>
                  <Skeleton className="h-24" />
                </TD>
              </TR>
            )}
            {data?.length === 0 && <EmptyRow colSpan={5}>Noch keine Formulare – starten Sie mit einer Vorlage.</EmptyRow>}
            {data?.map((f) => (
              <TR key={f.id} className="cursor-pointer" onClick={() => navigate(canManage ? `/forms/${f.id}` : `/forms/${f.id}/submissions`)}>
                <TD>
                  <p className="font-medium">{f.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">/f/{f.slug}</p>
                </TD>
                <TD className="hidden md:table-cell">
                  <div className="flex gap-1">
                    <Badge variant="secondary">{KIND_LABELS[f.kind]}</Badge>
                    <Badge variant="outline">{ACCESS_LABELS[f.access]}</Badge>
                  </div>
                </TD>
                <TD>
                  {!f.active ? (
                    <Badge variant="outline">deaktiviert</Badge>
                  ) : f.publishedVersion ? (
                    <Badge variant="success">Version {f.publishedVersion.version}</Badge>
                  ) : (
                    <Badge variant="warning">Entwurf</Badge>
                  )}
                  {f.publishedVersion && f.hasUnpublishedChanges && <span className="ml-1 text-xs text-muted-foreground">+ Änderungen</span>}
                </TD>
                <TD className="hidden text-right tabular-nums sm:table-cell">{f.submissionCount}</TD>
                <TD onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="size-8" aria-label={`Aktionen für ${f.name}`}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canManage && (
                        <DropdownMenuItem onSelect={() => navigate(`/forms/${f.id}`)}>
                          <Pencil /> Bearbeiten
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onSelect={() => navigate(`/forms/${f.id}/submissions`)}>
                        <Inbox /> Einsendungen
                      </DropdownMenuItem>
                      {f.access === 'PUBLIC' && (
                        <DropdownMenuItem onSelect={() => setEmbed(f)}>
                          <Code2 /> Link und Einbettung
                        </DropdownMenuItem>
                      )}
                      {canManage && (
                        <>
                          <DropdownMenuItem onSelect={() => duplicate.mutate(f)}>
                            <Copy /> Duplizieren
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            destructive
                            disabled={f.submissionCount > 0}
                            onSelect={async () => (await confirm({ title: `„${f.name}“ löschen?`, confirmLabel: 'Löschen', destructive: true })) && remove.mutate(f.id)}
                          >
                            <Trash2 /> Löschen
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      {embed && <EmbedDialog form={embed} open onOpenChange={(o) => !o && setEmbed(null)} />}
    </>
  );
}

/** Antworten einer Einsendung mit den Beschriftungen der damals gültigen Formularversion. */
export function SubmissionAnswers({ submission }: { submission: FormSubmissionItem }) {
  const parsed = formDefinitionSchema.safeParse(submission.definition);
  if (!parsed.success) return <pre className="text-xs">{JSON.stringify(submission.data, null, 2)}</pre>;
  const def = parsed.data;
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(0,14rem)_1fr]">
      {inputFields(def)
        .filter((f) => submission.data[f.key] !== undefined)
        .map((f) => (
          <div key={f.key} className="contents">
            <dt className="text-muted-foreground">{f.label}</dt>
            <dd className="whitespace-pre-wrap break-words">
              {f.type === 'file'
                ? submission.files.map((file) => (
                    <a key={file.id} className="mr-2 inline-flex items-center gap-1 text-primary hover:underline" href={`/api/forms/submissions/${submission.id}/files/${file.id}`} target="_blank" rel="noreferrer">
                      <FileText className="size-3.5" /> {file.name}
                    </a>
                  ))
                : displayValue(f, submission.data[f.key] as never)}
            </dd>
          </div>
        ))}
    </dl>
  );
}

export function SubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<FormSubmissionItem | null>(null);
  const { data: forms } = useForms();
  const form = forms?.find((f) => f.id === id);
  const { data } = useQuery({
    queryKey: ['forms', 'submissions', id, page],
    queryFn: () => api<Paginated<FormSubmissionItem>>(`/forms/submissions?formId=${id}&page=${page}&pageSize=25`),
  });
  return (
    <>
      <Link to="/forms" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Formulare
      </Link>
      <PageHeader title={`Einsendungen: ${form?.name ?? ''}`} />
      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Eingang</TH>
              <TH>Von</TH>
              <TH className="hidden md:table-cell">Quelle</TH>
              <TH>Vorgang</TH>
            </TR>
          </THead>
          <TBody>
            {data?.items.length === 0 && <EmptyRow colSpan={4}>Noch keine Einsendungen.</EmptyRow>}
            {data?.items.map((s) => (
              <TR key={s.id} className="cursor-pointer" onClick={() => setSelected(s)}>
                <TD className="whitespace-nowrap" title={formatDateTime(s.createdAt)}>
                  {formatRelative(s.createdAt)}
                  <span className="block text-xs text-muted-foreground">Version {s.version}</span>
                </TD>
                <TD>
                  <p>{s.submitterName ?? '–'}</p>
                  <p className="text-xs text-muted-foreground">{s.submitterEmail}</p>
                </TD>
                <TD className="hidden md:table-cell">{SOURCE_LABELS[s.source]}</TD>
                <TD onClick={(e) => e.stopPropagation()}>
                  {s.status === 'PENDING_CONFIRMATION' ? (
                    <Badge variant="warning">wartet auf E-Mail-Bestätigung</Badge>
                  ) : s.ticket ? (
                    <Link to={`/tickets/${s.ticket.id}`} className="font-mono text-sm text-primary hover:underline">
                      {s.ticket.number}
                    </Link>
                  ) : s.repair ? (
                    <Link to={`/repairs/${s.repair.id}`} className="font-mono text-sm text-primary hover:underline">
                      {s.repair.number}
                    </Link>
                  ) : (
                    '–'
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent wide>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Einsendung vom {formatDateTime(selected.createdAt)}</DialogTitle>
                <DialogDescription>
                  {SOURCE_LABELS[selected.source]} · Formularversion {selected.version}
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <SubmissionAnswers submission={selected} />
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
